import React from 'react'
import { Alert, Select, Tabs } from 'antd'
import { Link } from 'react-router'
import { ListPage } from '../../theme/listkit'
import { monthLabel, upcomingMonths, addMonths, upcomingSpotlightMonth } from './botm'
import { NominationsTab } from './NominationsTab'
import { EligibilityTab } from './EligibilityTab'
import { ScoringTab } from './ScoringTab'
import { ScheduleTab } from './ScheduleTab'
import { useJaguarOwnedSwitches } from '../jaguar_owned_controls/useJaguarOwnedSwitches'

/**
 * Business of the Month console — the committee's home for running the
 * monthly spotlight: who was nominated, who's eligible, how they scored
 * (ScoringTab, with recusal), and what's scheduled (ScheduleTab, via
 * SpotlightForm's Drawer + the share checklist). One month at a time, picked
 * from the selector above the tabs; every tab reads that same month.
 *
 * The month selector defaults to `upcomingSpotlightMonth`, NOT
 * `nominationTargetMonth` — Storyboard F3 opens the console on the month the
 * committee is actively working ("October 2026 · nominations closed September
 * 5"), which is a month BEHIND the still-open nomination window.
 */
/**
 * The switch banner's message: null when the hub is fully open AND the
 * Spotlight row is on (nothing to say), the hub-off message when the hub
 * itself is off (which hides Spotlight regardless of its own row —
 * `jaguar_owned_flag()` composes them the same way), the admin-only-preview
 * message when the hub is on but scoped to admins (Task S3's
 * `jaguar_owned_admins_only` — see `semantics.md` in that task's SDD
 * folder), otherwise the Spotlight-specific message. `null` while the
 * switches haven't loaded successfully yet (still loading, or the fetch
 * failed) — the console never flashes a wrong "off" banner before the real
 * rows arrive, and never claims to know the switch state off a failed read.
 *
 * Before Task S3, `hubOn` alone decided the first branch, and the doc
 * comment above claimed "null when both the hub and Spotlight rows are on
 * — nothing to say". That claim broke the moment a hub row could be `true`
 * while still being invisible to members (Admins only): `hubOn &&
 * spotlightOn` would fall through to `null`, telling the committee nothing
 * was hidden when in fact everyone but admins was shut out. `hubAdminsOnly`
 * is threaded through for exactly that case, so the banner keeps telling
 * the truth in preview.
 *
 * FIX ROUND (M3, 2026-09-18): the preview branch used to `return` before
 * ever checking `spotlightOn`, so with BOTH the hub in Admins only AND the
 * Spotlight row off, the banner said only "admin-only preview" and stayed
 * silent about Spotlight itself being off too — even though that second
 * fact changes who can see spotlights (nobody at all, not even admins,
 * since the Spotlight row gates everyone unconditionally, while the preview
 * alone still lets an admin through). The two conditions are independent —
 * either can be true without the other — so the message now COMPOSES both
 * sentences when both apply, instead of the first one found winning.
 */
function switchBannerMessage(ready: boolean, hubOn: boolean, hubAdminsOnly: boolean, spotlightOn: boolean): string | null {
  if (!ready) return null
  if (!hubOn) {
    return "Jaguar-Owned is off. Members and owners don't see Business of the Month; you can still prepare spotlights."
  }
  if (hubAdminsOnly && !spotlightOn) {
    return "Jaguar-Owned is in admin-only preview, and Business of the Month is also off — nobody, including admins, sees spotlights or nominations right now; owners keep their own listing, and you can still prepare spotlights."
  }
  if (hubAdminsOnly) {
    return 'Jaguar-Owned is in admin-only preview. Members don\'t see Business of the Month yet; owners keep their own listing, and you can still prepare spotlights.'
  }
  if (!spotlightOn) {
    return "Business of the Month is off. Members and owners don't see spotlights or nominations; you can still prepare them."
  }
  return null
}

export function BusinessOfMonthPage() {
  const target = React.useMemo(() => upcomingSpotlightMonth(new Date()), [])
  const monthOptions = React.useMemo(() => upcomingMonths(addMonths(target, -1), 6), [target])
  const [month, setMonth] = React.useState(target)

  const { byKey, isLoading: switchesLoading, isError: switchesError } = useJaguarOwnedSwitches()
  const hubOn = byKey.get('jaguar_owned_enabled')?.enabled ?? false
  // A missing `jaguar_owned_admins_only` row reads as "not in preview" —
  // open to everyone — the same default `semantics.md` documents for the
  // controls page's own hub audience control.
  const hubAdminsOnly = byKey.get('jaguar_owned_admins_only')?.enabled ?? false
  const spotlightOn = byKey.get('jaguar_owned_spotlight_enabled')?.enabled ?? false
  const bannerMessage = switchBannerMessage(!switchesLoading && !switchesError, hubOn, hubAdminsOnly, spotlightOn)

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Jaguar-Owned',
        description:
          "Members nominate alumni businesses; the committee checks eligibility, scores against the published rubric, and schedules one spotlight a month. A business can't win again for 24 months.",
      }}
    >
      {/* Capped so an ultrawide admin display (3440px) doesn't stretch the
          month select and tables across the full viewport width. */}
      <div style={{ maxWidth: 1200 }}>
        {bannerMessage ? (
          <Alert
            type="warning"
            showIcon
            data-testid="botm-switch-banner"
            style={{ maxWidth: 720, marginBottom: 16 }}
            message={
              <span>
                {bannerMessage} <Link to="/jaguar-owned/controls">Open Jaguar-Owned switches</Link>
              </span>
            }
          />
        ) : null}
        <div style={{ marginBottom: 16 }}>
          <Select
            data-testid="botm-month"
            value={month}
            onChange={setMonth}
            style={{ width: 220 }}
            options={monthOptions.map((m) => ({ value: m, label: monthLabel(m) }))}
          />
        </div>
        <Tabs
          defaultActiveKey="nominations"
          items={[
            { key: 'nominations', label: 'Nominations', children: <NominationsTab month={month} /> },
            { key: 'eligibility', label: 'Eligibility', children: <EligibilityTab /> },
            { key: 'scoring', label: 'Scoring', children: <ScoringTab month={month} /> },
            { key: 'schedule', label: 'Schedule', children: <ScheduleTab month={month} /> },
          ]}
        />
      </div>
    </ListPage>
  )
}
